import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { usePhysicalAtonMutation } from './Mutations/usePhysicalAtonMutation';
import { UseFormReturn } from 'react-hook-form';
import { AtoN } from '@ais-ui/api-spec/config';
import { useLocalization } from '@ais-ui/locale-management';

interface AtonContextProps {
  children: ReactNode;
}

interface AtonContextValue {
  showDialog: boolean;
  setShowDialog: Dispatch<SetStateAction<boolean>>;
  atonType: string;
  setAtonType: Dispatch<SetStateAction<string>>;
  physicalForm: UseFormReturn<any>;
  onCreatePhysical: (data: any) => void;
  isSubmitting: boolean;
  onSelectAtonOnMap: () => void;
  mutateType: string;
  setMutateType: Dispatch<SetStateAction<string>>;
  onUpdatePhysical: (data: any) => void;
  aton: AtoN | null;
  setAton: Dispatch<SetStateAction<AtoN | null>>;
  showCreateVirtualAton: boolean;
  showCreatePhysicalAton: boolean;
  showEditVirtualAton: boolean;
  showEditPhysicalAton: boolean;
}

const AtonContext = createContext<AtonContextValue | undefined>(undefined);

export const AtonProvider = ({ children
}: AtonContextProps) => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [atonType, setAtonType] = useState<string>('');
  const [mutateType, setMutateType] = useState<string>('');
  const [aton, setAton] = useState<AtoN | null>(null);

  const {
    onCreatePhysical,
    physicalForm,
    onSelectAtonOnMap,
    onUpdatePhysical,
  } = usePhysicalAtonMutation(setIsSubmitting, setShowDialog, aton, setAton);

  const showCreateVirtualAton = useMemo(() => {
    return atonType === 'virtual' && showDialog && mutateType === 'create';
  }, [atonType, showDialog, mutateType]);

  const showCreatePhysicalAton = useMemo(() => {
    return atonType === 'physical' && showDialog && mutateType === 'create';
  }, [atonType, showDialog, mutateType]);

  const showEditVirtualAton = useMemo(() => {
    return (
      atonType === 'virtual' && showDialog && mutateType === 'edit' && !!aton
    );
  }, [atonType, showDialog, mutateType, aton]);

  const showEditPhysicalAton = useMemo(() => {
    return (
      atonType === 'physical' && showDialog && mutateType === 'edit' && !!aton
    );
  }, [atonType, showDialog, mutateType, aton]);

  return (
    <AtonContext.Provider
      value={{
        showDialog,
        setShowDialog,
        atonType,
        setAtonType,
        physicalForm,
        onCreatePhysical,
        isSubmitting,
        onSelectAtonOnMap,
        mutateType,
        setMutateType,
        onUpdatePhysical,
        aton,
        setAton,
        showCreateVirtualAton,
        showCreatePhysicalAton,
        showEditVirtualAton,
        showEditPhysicalAton,
      }}
    >
      {children}
    </AtonContext.Provider>
  );
};

export const useAtonContext = () => {
  const { messages } = useLocalization();
  const context = useContext(AtonContext);
  if (!context) {
    throw new Error(messages.atonUseAtonContextErrorMessage); //'useAtonContext must be used within a AtonProvider');
  }
  return context;
};
